import { useQueries, useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { PlayCircle, Zap } from 'lucide-react';
import { engagementsApi, findingsApi, runsApi } from '../lib/api-client';
import { PageHeader } from '../components/layout/PageHeader';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { LoadingState, ErrorState, EmptyState } from '../components/ui/states';
import { RUN_STATUS_VARIANT } from '../lib/run-status';

/**
 * Unified scan history (replaces the old Runs nav item — see FIX4 of the navigation
 * simplification pass). "Target" is the engagement name rather than a dedicated field: Run has no
 * target of its own, and Quick Scan already names every engagement it creates
 * `Quick Scan — <target>` (see QuickScan.tsx's runQuickScan), so the engagement name IS the
 * target for the overwhelming majority of scans. Findings count is per-ENGAGEMENT
 * (findingsApi.forEngagement), not per-run — a reasonable proxy since Quick Scan creates one
 * engagement per scan; an engagement with multiple runs (e.g. after a retest) shows the
 * engagement's total across all of them, not just this one run's contribution.
 */
export function Scans() {
  const engagementsQuery = useQuery({ queryKey: ['engagements'], queryFn: () => engagementsApi.list() });
  const engagements = engagementsQuery.data?.engagements ?? [];

  const runQueries = useQueries({
    queries: engagements.map((e) => ({
      queryKey: ['runs', e.id],
      queryFn: () => runsApi.list(e.id),
      enabled: engagements.length > 0,
    })),
  });

  const findingsQueries = useQueries({
    queries: engagements.map((e) => ({
      queryKey: ['findings', 'engagement', e.id],
      queryFn: () => findingsApi.forEngagement(e.id),
      enabled: engagements.length > 0,
    })),
  });

  if (engagementsQuery.isLoading) return <LoadingState />;
  if (engagementsQuery.isError) return <ErrorState error={engagementsQuery.error} onRetry={() => engagementsQuery.refetch()} />;

  const rows = engagements
    .flatMap((e, i) => (runQueries[i]?.data?.runs ?? []).map((run) => ({ engagement: e, run, findingsCount: findingsQueries[i]?.data?.total })))
    .sort((a, b) => new Date(b.run.createdAt).getTime() - new Date(a.run.createdAt).getTime());

  return (
    <div>
      <PageHeader
        title="Scans"
        description="Every scan across your workspace, most recent first."
        action={
          <Button asChild>
            <Link to="/quick-scan">
              <Zap size={16} /> New Scan
            </Link>
          </Button>
        }
      />
      {rows.length === 0 ? (
        <EmptyState
          icon={<PlayCircle size={32} />}
          title="No scans yet"
          description="Start your first scan to see it here."
          action={
            <Link to="/quick-scan" className="text-sm font-medium text-brand underline-offset-2 hover:underline">
              Start a scan
            </Link>
          }
        />
      ) : (
        <div className="space-y-2">
          {rows.map(({ engagement, run, findingsCount }) => (
            <Link
              key={run.id}
              to={`/runs/${run.id}`}
              className="flex items-center justify-between gap-4 rounded-xl border border-border bg-surface-raised p-4 text-sm hover:bg-surface-sunken"
            >
              <div className="min-w-0">
                <p className="truncate font-medium text-ink">{engagement.name}</p>
                <p className="text-ink-muted">{new Date(run.createdAt).toLocaleString()}</p>
              </div>
              <div className="flex shrink-0 items-center gap-3">
                <span className="text-ink-muted">
                  {findingsCount === undefined ? '…' : `${findingsCount} finding${findingsCount === 1 ? '' : 's'}`}
                </span>
                <Badge variant={RUN_STATUS_VARIANT[run.status]} className="capitalize">
                  {run.status}
                </Badge>
              </div>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}
